import React, { useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Track } from "../../data/schema";
import { showToast } from "components/ui-kit/Toast";

type Props = {
  open: boolean;
  track: Track | null;
  onClose: () => void;
};

const REPORT_REASONS = [
  "Low audio quality",
  "Doesn't follow lyrics",
  "Doesn't follow style instructions",
  "Poor structure",
  "Other",
] as const;

export const ReportBugModal: React.FC<Props> = ({ open, track, onClose }) => {
  const [selectedReasons, setSelectedReasons] = useState<Set<string>>(new Set());

  const handleReasonToggle = (reason: string) => {
    const newSelected = new Set(selectedReasons);
    if (newSelected.has(reason)) {
      newSelected.delete(reason);
    } else {
      newSelected.add(reason);
    }
    setSelectedReasons(newSelected);
  };

  const handleSubmit = () => {
    if (selectedReasons.size === 0) {
      showToast("Please select at least one reason", undefined, 'error');
      return;
    }
    
    // Here you would typically send the report to the backend
    // For now, just show a success message
    showToast("Report submitted successfully. Thank you!", undefined, 'success');
    setSelectedReasons(new Set());
    onClose();
  };

  const handleCancel = () => {
    setSelectedReasons(new Set());
    onClose();
  };

  if (!open || !track) return null;

  const content = (
    <Overlay onClick={handleCancel}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Please provide reasons for flagging {track.title || 'this track'}</Title>
        </Header>
        
        <ReasonsList>
          {REPORT_REASONS.map((reason) => (
            <ReasonItem key={reason}>
              <Checkbox
                type="checkbox"
                checked={selectedReasons.has(reason)}
                onChange={() => handleReasonToggle(reason)}
                id={`reason-${reason}`}
              />
              <Label htmlFor={`reason-${reason}`}>
                {reason}
              </Label>
            </ReasonItem>
          ))}
        </ReasonsList>

        <Actions>
          <CancelButton onClick={handleCancel}>
            Cancel
          </CancelButton>
          <SubmitButton onClick={handleSubmit}>
            Submit
          </SubmitButton>
        </Actions>
      </Dialog>
    </Overlay>
  );

  if (typeof document === "undefined") {
    return content;
  }
  return createPortal(content, document.body);
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Dialog = styled.div`
  width: 500px;
  max-width: calc(100% - 40px);
  background: #fff;
  color: #000;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;

  @media (max-width: 640px) {
    width: 100%;
    max-width: none;
    margin: 0 16px;
    padding: 20px;
  }
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #000;
  line-height: 1.4;
`;

const ReasonsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`;

const ReasonItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #6777FF;
  flex-shrink: 0;
`;

const Label = styled.label`
  font-size: 14px;
  color: #333;
  cursor: pointer;
  user-select: none;
  flex: 1;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #333;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: #f4f4f4;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 24px;
  border-radius: 16px;
  border: none;
  background: linear-gradient(90deg, #7987FF 0%, #FF7B7D 100%);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:active {
    opacity: 0.8;
  }
`;

export default ReportBugModal;

